<template>
    <div ref="rootElement" class="cadastral-map-container">
        <div class="map-wrapper">
            <LMap :center="centerLatLng"
                  :zoom="zoom"
                  :options="mapOptions"
                  class="cadastral-map"
                  ref="map"
                  @update:zoom="updateZoom"
                  @update:bounds="handleBoundsChange"
                  @ready="onMapReady">
                <LControlLayers position="topright" @ready="onLayersControlReady"></LControlLayers>
                <MapyCzBasicLayer/>
                <MapyCzOrthoPhotoLayer :visible="false"/>
                <LControlZoom position="topleft"/>
                <LControl position="bottomleft" v-if="loading">
                    <div class="map-spinner-control">
                        <div class="spinner-border spinner-border-sm" role="status"></div>
                    </div>
                </LControl>
                <LGeoJson
                    v-if="cadastralUnitsFeatureCollection.features.length > 0"
                    :key="`cadastral-units-${zoom}`"
                    :geojson="cadastralUnitsFeatureCollection"
                    :options="geoJsonOptions"
                    :options-style="geoJsonStyleFunction"/>
            </LMap>
            <div v-if="zoomTooSmall" class="zoom-prompt-overlay">
                <div class="zoom-prompt-message">
                    Pro zobrazení katastrálních území přibližte mapu
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import MapyCzBasicLayer from '@/components/maps/MapyCzBasicLayer.vue';
import MapyCzOrthoPhotoLayer from '@/components/maps/MapyCzOrtophotoLayer.vue';
import {log} from '@/model/logger';
import {publishLeafletMap, unpublishLeafletMap} from '@/shared/leafletMapRegistry';
import L from 'leaflet';
import {computed, defineComponent, nextTick, onBeforeUnmount, onMounted, PropType, ref, watch} from 'vue';
import {LControl, LControlLayers, LControlZoom, LGeoJson, LMap} from '@vue-leaflet/vue-leaflet';

interface Coordinates {
    readonly latitude: number;
    readonly longitude: number;
}

interface CadastralUnit {
    readonly id: string;
    readonly name: string;
    readonly geometry: GeoJSON.Geometry;
}

interface BoundingBox {
    readonly southWest: {
        readonly longitude: number;
        readonly latitude: number;
    };
    readonly northEast: {
        readonly longitude: number;
        readonly latitude: number;
    };
}

interface CadastralUnitsRequest {
    readonly zoom: number;
    readonly boundingBox: BoundingBox;
}

interface CadastralUnitsResponse {
    readonly id: string;
    readonly name: string;
    readonly geometry: GeoJSON.Geometry;
}

const MIN_ZOOM = 12;
const DEFAULT_ZOOM = 12;
const GEOLOCATION_TIMEOUT = 5000;
const GEOLOCATION_MAX_AGE = 60000;
const FLY_TO_DURATION = 1;
const MAX_CACHED_UNITS = 2000;
const DEBOUNCE_MS = 500;

const STYLE_COLORS = {
    border: '#9333ea',
    fill: '#a855f7',
};

const STYLE_DEFAULT = {
    weight: 1,
    opacity: 0.8,
};

const STYLE_HOVER = {
    weight: 2,
    opacity: 1,
};

const STYLE_SELECTED = {
    fillOpacity: 0.25,
};

const STYLE_UNSELECTED = {
    fillOpacity: 0,
};

export default defineComponent({
    components: {
        MapyCzOrthoPhotoLayer,
        MapyCzBasicLayer,
        LMap,
        LGeoJson,
        LControl,
        LControlLayers,
        LControlZoom,
    },
    props: {
        center: {
            type: Object as PropType<Coordinates>,
            required: true,
        },
        loadPointsUrl: {
            type: String,
            required: true,
        },
        hiddenFieldName: {
            type: String,
            required: false,
        },
        initialSelectedIds: {
            type: String,
            default: '',
        },
        useGeolocation: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['selection-changed'],
    setup(props, {emit}) {
        const rootElement = ref<HTMLElement | null>(null);
        const mapRef = ref<any>(null);
        const leafletMap = ref<L.Map | null>(null);
        const zoom = ref(DEFAULT_ZOOM);
        const loading = ref(false);
        const visibleCadastralUnits = ref<CadastralUnit[]>([]);
        const selectedUnits = ref<Record<string, boolean>>(parseInitialSelectedIds());

        let previousAbortController: AbortController | null = null;
        const cadastralUnitsCache: Map<number, Map<string, CadastralUnit>> = new Map();
        const loadedBoundsAreas: Map<number, L.LatLngBounds[]> = new Map();
        let visibilityObserver: IntersectionObserver | null = null;
        let isAnimating = false;
        let boundsChangeDebounceTimer: number | null = null;

        // Computed
        const centerLatLng = computed((): L.LatLng => {
            return L.latLng(props.center.latitude, props.center.longitude);
        });

        const zoomTooSmall = computed((): boolean => {
            return zoom.value < MIN_ZOOM;
        });

        const mapOptions = computed((): L.MapOptions => {
            return {
                zoomSnap: 0.25,
                preferCanvas: false,
                zoomControl: false,
            };
        });

        const selectedUnitIds = computed((): string[] => {
            return Object.keys(selectedUnits.value).filter(id => selectedUnits.value[id]);
        });

        const cadastralUnitsFeatureCollection = computed((): GeoJSON.FeatureCollection => {
            return {
                type: 'FeatureCollection',
                features: visibleCadastralUnits.value.map(unit => ({
                    type: 'Feature',
                    id: unit.id,
                    geometry: unit.geometry,
                    properties: {
                        id: unit.id,
                        name: unit.name,
                        selected: !!selectedUnits.value[unit.id],
                    },
                })),
            };
        });

        const geoJsonOptions = computed((): L.GeoJSONOptions => {
            return {
                onEachFeature: (feature, layer) => configureFeatureFromFeatureCollection(feature, layer),
            };
        });

        const geoJsonStyleFunction = (feature?: GeoJSON.Feature) => getFeatureStyle(feature);

        watch(selectedUnitIds, (newSelectedIds) => {
            updateHiddenField(newSelectedIds);
        });

        // Map events
        function updateZoom(newZoom: number): void {
            zoom.value = newZoom;
            updateVisibleUnits();
        }

        function handleBoundsChange(): void {
            if (isAnimating) {
                return;
            }

            const currentBounds = leafletMap.value?.getBounds();
            if (currentBounds && currentBounds.isValid() && !zoomTooSmall.value && isAreaLoaded(currentBounds)) {
                updateVisibleUnits();
                if (boundsChangeDebounceTimer !== null) {
                    window.clearTimeout(boundsChangeDebounceTimer);
                    boundsChangeDebounceTimer = null;
                }
                return;
            }

            if (boundsChangeDebounceTimer !== null) {
                window.clearTimeout(boundsChangeDebounceTimer);
            }
            boundsChangeDebounceTimer = window.setTimeout(() => {
                doHandleBoundsChange();
            }, DEBOUNCE_MS);
        }

        async function doHandleBoundsChange(): Promise<void> {
            if (isAnimating) {
                return;
            }

            const abortController = new AbortController();

            try {
                loading.value = true;
                abortPreviousRequest();
                previousAbortController = abortController;

                await refreshCadastralUnits(abortController.signal);
            } catch (error) {
                if (!abortController.signal.aborted) {
                    log(error instanceof Error ? error.message : String(error));
                }
            } finally {
                if (!abortController.signal.aborted) {
                    loading.value = false;
                }
            }
        }

        function toggleUnitSelection(unit: CadastralUnit): void {
            if (selectedUnits.value[unit.id]) {
                const newUnits = {...selectedUnits.value};
                delete newUnits[unit.id];
                selectedUnits.value = newUnits;
            } else {
                selectedUnits.value = {...selectedUnits.value, [unit.id]: true};
            }
            emitSelectionChange();
        }

        // Visibility
        function waitForVisibility(): void {
            const element = rootElement.value!;

            visibilityObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        nextTick(() => {
                            if (leafletMap.value) {
                                leafletMap.value.invalidateSize();
                            }

                            setTimeout(() => {
                                tryUseUserLocation();
                            }, 100);

                            doHandleBoundsChange();
                        });

                        if (visibilityObserver) {
                            visibilityObserver.disconnect();
                            visibilityObserver = null;
                        }
                    }
                });
            }, {
                threshold: 0.1,
            });

            visibilityObserver.observe(element);
        }

        // Feature styling
        function getFeatureStyle(feature?: GeoJSON.Feature): L.PathOptions {
            const isSelected = feature?.properties?.selected ?? false;

            return {
                color: STYLE_COLORS.border,
                weight: STYLE_DEFAULT.weight,
                opacity: STYLE_DEFAULT.opacity,
                fillColor: STYLE_COLORS.fill,
                fillOpacity: isSelected
                    ? STYLE_SELECTED.fillOpacity
                    : STYLE_UNSELECTED.fillOpacity,
            };
        }

        function configureFeatureFromFeatureCollection(feature: GeoJSON.Feature, layer: L.Layer): void {
            const unitId = feature.properties?.id;

            if (!unitId) {
                return;
            }

            const cache = cadastralUnitsCache.get(zoom.value);
            const unit = cache?.get(unitId);

            if (!unit) {
                return;
            }

            bindTooltip(unit, layer);
            attachEventListeners(unit, layer);
        }

        function emitSelectionChange(): void {
            emit('selection-changed', selectedUnitIds.value);
            updateHiddenField(selectedUnitIds.value);
        }

        // User location
        function tryUseUserLocation(): void {
            if (!props.useGeolocation) {
                return;
            }

            if (!navigator.geolocation) {
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => handleGeolocationSuccess(position),
                (error) => handleGeolocationError(error),
                {
                    timeout: GEOLOCATION_TIMEOUT,
                    maximumAge: GEOLOCATION_MAX_AGE,
                },
            );
        }

        function handleGeolocationSuccess(position: GeolocationPosition): void {
            const latitude = position.coords.latitude;
            const longitude = position.coords.longitude;

            if (!isFinite(latitude) || !isFinite(longitude)) {
                log('Invalid geolocation coordinates:', {latitude, longitude});
                return;
            }

            const userLocation = L.latLng(latitude, longitude);

            if (!leafletMap.value) {
                log('Map not ready for flyTo');
                return;
            }

            isAnimating = true;

            const onMoveEnd = () => {
                isAnimating = false;
                leafletMap.value!.off('moveend', onMoveEnd);
                doHandleBoundsChange();
            };

            leafletMap.value.on('moveend', onMoveEnd);

            leafletMap.value.flyTo(userLocation, DEFAULT_ZOOM, {
                duration: FLY_TO_DURATION,
            });
        }

        function handleGeolocationError(error: GeolocationPositionError): void {
            log('Geolocation error:', error.message);
        }

        function abortPreviousRequest(): void {
            previousAbortController?.abort();
        }

        async function refreshCadastralUnits(signal: AbortSignal): Promise<void> {
            if (zoomTooSmall.value) {
                visibleCadastralUnits.value = [];
                return;
            }

            const currentBounds = leafletMap.value!.getBounds();

            if (!currentBounds || !currentBounds.isValid()) {
                return;
            }

            if (isAreaLoaded(currentBounds)) {
                updateVisibleUnits();
                return;
            }

            const units = await fetchCadastralUnits(currentBounds, signal);
            evictCacheIfNeeded(units.length);
            addUnitsToCache(units);
            expandLoadedBounds(currentBounds);
            updateVisibleUnits();
        }

        function isAreaLoaded(bounds: L.LatLngBounds): boolean {
            const loadedAreas = loadedBoundsAreas.get(zoom.value);

            if (!loadedAreas || loadedAreas.length === 0) {
                return false;
            }

            return loadedAreas.some((loadedBounds) => loadedBounds.contains(bounds));
        }

        function expandLoadedBounds(newBounds: L.LatLngBounds): void {
            if (!loadedBoundsAreas.has(zoom.value)) {
                loadedBoundsAreas.set(zoom.value, []);
            }

            const loadedAreas = loadedBoundsAreas.get(zoom.value)!;
            const newBoundsObj = L.latLngBounds(
                newBounds.getSouthWest(),
                newBounds.getNorthEast(),
            );

            let merged = false;
            for (const existingBounds of loadedAreas) {
                if (boundsOverlapOrAdjacent(existingBounds, newBoundsObj)) {
                    existingBounds.extend(newBoundsObj);
                    merged = true;
                    break;
                }
            }

            if (!merged) {
                loadedAreas.push(newBoundsObj);
            }
        }

        function boundsOverlapOrAdjacent(bounds1: L.LatLngBounds, bounds2: L.LatLngBounds): boolean {
            const margin = 0.01;

            const bounds1Extended = L.latLngBounds(
                L.latLng(bounds1.getSouth() - margin, bounds1.getWest() - margin),
                L.latLng(bounds1.getNorth() + margin, bounds1.getEast() + margin),
            );

            return bounds1Extended.intersects(bounds2);
        }

        function evictCacheIfNeeded(incomingCount: number): void {
            let totalCached = 0;
            cadastralUnitsCache.forEach((cache) => {
                totalCached += cache.size;
            });

            if (totalCached + incomingCount > MAX_CACHED_UNITS) {
                log(`Cache limit reached (${totalCached} + ${incomingCount} > ${MAX_CACHED_UNITS}), clearing cache`);
                cadastralUnitsCache.clear();
                loadedBoundsAreas.clear();
            }
        }

        function addUnitsToCache(units: CadastralUnit[]): void {
            if (!cadastralUnitsCache.has(zoom.value)) {
                cadastralUnitsCache.set(zoom.value, new Map());
            }

            const cache = cadastralUnitsCache.get(zoom.value)!;
            units.forEach((unit) => {
                cache.set(unit.id, unit);
            });
        }

        function updateVisibleUnits(): void {
            const cache = cadastralUnitsCache.get(zoom.value);
            if (!cache) {
                visibleCadastralUnits.value = [];
                return;
            }

            visibleCadastralUnits.value = Array.from(cache.values());
        }

        async function fetchCadastralUnits(bounds: L.LatLngBounds, signal: AbortSignal): Promise<CadastralUnit[]> {
            const requestData = buildRequestData(bounds);
            const response = await sendRequest(requestData, signal);
            const jsonResponse = await response.json();

            return parseJsonResponse(jsonResponse);
        }

        function buildRequestData(bounds: L.LatLngBounds): CadastralUnitsRequest {
            return {
                zoom: zoom.value,
                boundingBox: {
                    southWest: {
                        longitude: bounds.getSouthWest().lng,
                        latitude: bounds.getSouthWest().lat,
                    },
                    northEast: {
                        longitude: bounds.getNorthEast().lng,
                        latitude: bounds.getNorthEast().lat,
                    },
                },
            };
        }

        async function sendRequest(requestData: CadastralUnitsRequest, signal: AbortSignal): Promise<Response> {
            const response = await fetch(props.loadPointsUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestData),
                signal,
            });

            if (!response.ok) {
                throw new Error(`Cadastral units refresh failed with status ${response.status}`);
            }

            return response;
        }

        function parseJsonResponse(jsonResponse: CadastralUnitsResponse[]): CadastralUnit[] {
            return jsonResponse.map((item) => mapJsonDataToCadastralUnit(item));
        }

        function mapJsonDataToCadastralUnit(item: CadastralUnitsResponse): CadastralUnit {
            return {
                id: item.id,
                name: item.name,
                geometry: item.geometry,
            };
        }

        function bindTooltip(unit: CadastralUnit, layer: L.Layer): void {
            layer.bindTooltip(unit.name, {
                permanent: false,
                direction: 'auto',
            });
        }

        function attachEventListeners(unit: CadastralUnit, layer: L.Layer): void {
            layer.on('click', () => toggleUnitSelection(unit));
            layer.on('mouseover', () => handleMouseOver(layer));
            layer.on('mouseout', () => handleMouseOut(layer));
        }

        function handleMouseOver(layer: L.Layer): void {
            if (layer instanceof L.Path) {
                layer.setStyle(STYLE_HOVER);
            }
        }

        function handleMouseOut(layer: L.Layer): void {
            if (layer instanceof L.Path) {
                layer.setStyle(STYLE_DEFAULT);
            }
        }

        function parseInitialSelectedIds(): Record<string, boolean> {
            if (!props.initialSelectedIds || props.initialSelectedIds.trim() === '') {
                return {};
            }

            const ids = props.initialSelectedIds.split(',').map(id => id.trim()).filter(id => id !== '');
            const result: Record<string, boolean> = {};

            ids.forEach(id => {
                result[id] = true;
            });

            return result;
        }

        function updateHiddenField(selectedIds: string[]): void {
            if (!props.hiddenFieldName) {
                return;
            }

            const hiddenField = document.querySelector<HTMLInputElement>(`input[name="${props.hiddenFieldName}"]`);
            if (!hiddenField) {
                log(`Hidden field with name "${props.hiddenFieldName}" not found`);
                return;
            }

            hiddenField.value = selectedIds.join(',');

            const event = new Event('change', {bubbles: true});
            hiddenField.dispatchEvent(event);
        }

        let pendingLayersControl: L.Control.Layers | null = null;

        function onMapReady(mapInstance: L.Map): void {
            leafletMap.value = mapInstance;
            if (pendingLayersControl) {
                publishLeafletMap(mapInstance.getContainer(), mapInstance, pendingLayersControl);
            }
        }

        function onLayersControlReady(layersControlInstance: L.Control.Layers): void {
            pendingLayersControl = layersControlInstance;
            if (leafletMap.value) {
                publishLeafletMap(leafletMap.value.getContainer(), leafletMap.value as L.Map, layersControlInstance);
            }
        }

        onMounted(() => {
            waitForVisibility();
        });

        onBeforeUnmount(() => {
            if (boundsChangeDebounceTimer !== null) {
                window.clearTimeout(boundsChangeDebounceTimer);
            }
            if (visibilityObserver) {
                visibilityObserver.disconnect();
            }
            if (leafletMap.value) {
                unpublishLeafletMap(leafletMap.value.getContainer());
            }
        });

        return {
            map: mapRef,
            zoom,
            loading,
            centerLatLng,
            zoomTooSmall,
            mapOptions,
            cadastralUnitsFeatureCollection,
            geoJsonOptions,
            geoJsonStyleFunction,
            updateZoom,
            handleBoundsChange,
            rootElement,
            onMapReady,
            onLayersControlReady,
        };
    },
});
</script>

<style scoped>
.cadastral-map-container {
    height: 100%;
    width: 100%;
}

.map-wrapper {
    position: relative;
    height: 100%;
    width: 100%;
    overflow: hidden;
}

.cadastral-map {
    height: 100%;
    width: 100%;
}

.cadastral-map :deep(.leaflet-interactive) {
    outline: none !important;
}

.cadastral-map :deep(.leaflet-interactive:focus) {
    outline: none !important;
}

.map-spinner-control {
    background-color: white;
    padding: 8px;
    box-shadow: 0 1px 5px rgba(0,0,0,0.4);
    border-radius: 4px;
    pointer-events: none;
}

.zoom-prompt-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.85);
    z-index: 900;
    pointer-events: none;
}

.zoom-prompt-message {
    background-color: white;
    padding: 10px 16px;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    text-align: center;
    white-space: nowrap;
}
</style>
